import { CheckIcon, CopyIcon } from '@phosphor-icons/react'
import type { ReactNode } from 'react'

// The copy control wears the landing console's class, so this screen depends on
// that stylesheet. Imported rather than left to whichever screen rendered first:
// someone deep-linking here has loaded neither the landing page nor the webhooks
// signed-out screen, and the hover state would simply be missing.
import '../landing/landing.css'

import { CURRI_VERSION, useAuth } from '../../auth/AuthProvider.tsx'
import { CONSOLE } from '../../theme/console.ts'
import { useCopyToClipboard } from '../../useCopyToClipboard.ts'

import { SECRET_MASK } from './Secret.tsx'

// The landing page's dark console, reused to show the assembled header on the
// wire — the same object the hero draws, so a partner arriving from the landing
// page recognises it as "the code, for real this time".
//
// The request is `GET /api/me` because that endpoint exists to answer exactly
// the question someone on this screen is asking: did my credential work, and
// what may it do. It mutates nothing, needs no body, and is short enough to stay
// readable at half a card's width.
//
// The token follows the card's reveal state, but Copy always writes the *real*
// command. A pasted row of dots is a broken request with no explanation, and
// someone who clicked Copy has asked for the credential as plainly as clicking
// Reveal does.

const COPY_FEEDBACK_MS = 1600

const Note = ({ children }: { children: ReactNode }) => (
  <span style={{ color: CONSOLE.muted }}>{children}</span>
)

const Flag = ({ children }: { children: ReactNode }) => (
  <span style={{ color: CONSOLE.subdued }}>{children}</span>
)

const Str = ({ children }: { children: ReactNode }) => (
  <span style={{ color: CONSOLE.accent }}>{children}</span>
)

// The copyable text mirrors the rendered snippet exactly — same request, same
// version header — so what lands in a terminal is what the page showed.
const snippetText = (baseUrl: string, token: string): string =>
  `# Check what your credential resolves to
curl ${baseUrl}/api/me \\
  --header 'Curri-Version: ${CURRI_VERSION}' \\
  --header 'Authorization: Basic ${token}'`

export const CurlConsole = ({
  encoded,
  revealed,
}: {
  /** The real credential. Copied whether or not it is currently on screen. */
  encoded: string
  revealed: boolean
}) => {
  const { config } = useAuth()
  const { copied, copy } = useCopyToClipboard(COPY_FEEDBACK_MS)

  const token = revealed ? encoded : SECRET_MASK

  return (
    <div
      style={{
        background: CONSOLE.surface,
        border: `1px solid ${CONSOLE.line}`,
        borderRadius: 14,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '10px 10px 9px',
          borderBottom: `1px solid ${CONSOLE.line}`,
        }}
      >
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            height: 26,
            padding: '0 11px',
            borderRadius: 999,
            border: '1px solid rgba(250, 250, 249, 0.18)',
            background: 'rgba(250, 250, 249, 0.13)',
            color: CONSOLE.textStrong,
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          cURL
        </span>
        <span style={{ flex: 1 }} />
        <span
          style={{
            fontFamily: 'var(--curri-font-mono)',
            fontSize: 11,
            color: CONSOLE.muted,
          }}
        >
          GET /api/me
        </span>
        <button
          aria-label="Copy request"
          className="landing-console-copy"
          onClick={() => copy(snippetText(config.publicUrl, encoded))}
          style={{
            width: 28,
            height: 28,
            marginLeft: 4,
            borderRadius: 7,
            border: '1px solid rgba(255, 255, 255, 0.1)',
            background: 'transparent',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: copied ? CONSOLE.accent : CONSOLE.subdued,
          }}
          type="button"
        >
          {copied ? <CheckIcon size={14} /> : <CopyIcon size={14} />}
        </button>
      </div>

      {/* Wrapped rather than scrolled: this card exists to show the shape of the
          header, and a base64 token that runs off the right edge of a
          half-width column shows half of it. */}
      <pre
        style={{
          margin: 0,
          padding: '14px 16px',
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          fontFamily: 'var(--curri-font-mono)',
          fontSize: 11.5,
          lineHeight: 1.75,
          color: CONSOLE.text,
        }}
      >
        <Note># Check what your credential resolves to</Note>
        {`\ncurl ${config.publicUrl}/api/me \\\n  `}
        <Flag>--header</Flag> <Str>{`'Curri-Version: ${CURRI_VERSION}'`}</Str>
        {' \\\n  '}
        <Flag>--header</Flag> <Str>{`'Authorization: Basic ${token}'`}</Str>
      </pre>
    </div>
  )
}
